// The order's one barcode answer, as the surfaces away from it see it.
//
// The answer itself lives with the delivery choices: fulfillment's mandatory
// `barcode_resolution`, or the optional `apply_barcode` tick on Shipping and
// Pickup. The upload notice that flags a missing barcode sits under the artwork,
// several items up the page, so it is handed two things here rather than through
// every component between QuoteEdit and the upload slot: whether this order
// needs a barcode at all, and a way to take the customer straight to the answer.
// It never gets a control of its own — a second control on the same key would
// mount once per slot and duplicate the DOM id the failure summary scrolls to.

import { createContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  APPLY_BARCODE_HELP,
  APPLY_BARCODE_LABELS,
  orderBarcodeApplication,
} from 'shared';

export type ArtworkBarcodeContextValue = {
  // orderRequiresBarcode — the notice's Required vs Optional variant.
  required: boolean;
  // Scrolls to the barcode answer for the order's delivery mode, or to the mode
  // cards while none is picked. Absent on a read-only order.
  onAnswer?: () => void;
};

export const ArtworkBarcodeContext = createContext<ArtworkBarcodeContextValue>({
  required: false,
});

// The paid answer's wording for this order (orderBarcodeApplication), read by
// both the fulfillment radio and the Shipping/Pickup checkbox so the two say it
// the same way.
export function useApplyBarcodeWording(lineConfigs: readonly unknown[]): {
  label: string;
  help: string;
} {
  const { t } = useTranslation('quote');
  const kind = orderBarcodeApplication(lineConfigs);
  return {
    label: t(`orderServices.applyBarcode.${kind}.label`, { defaultValue: APPLY_BARCODE_LABELS[kind] }),
    help: t(`orderServices.applyBarcode.${kind}.help`, { defaultValue: APPLY_BARCODE_HELP[kind] }),
  };
}
